import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import { Button, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { titleLabel, type Appointment } from '../members/AppointmentsCard';

type Ref = { personId: string; name: string };
type Chart = {
  pastoral: Ref[];
  ministries: { id: string; name: string; kind: 'grupo' | 'raices' | 'servicio'; leader: Ref | null; coLeaders: Ref[]; members: number; cells: { id: string; name: string; representative: Ref | null; representatives?: Ref[]; members: number }[] }[];
};
const KIND: [Chart['ministries'][number]['kind'], string][] = [['grupo', 'Grupos'], ['raices', 'Raíces (enseñanza)'], ['servicio', 'Servicio']];

/** Organigrama: Iglesia → Pastoral → Ministerios → quien dirige → células o equipo. */
export function OrgChartPage() {
  const me = useMe();
  const churchName = me.contexts.find((c) => c.churchId === me.activeChurchId)?.churchName ?? 'Iglesia';
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Chart>(`/organization/churches/${me.activeChurchId}/org-chart`, signal) : Promise.resolve(null)), [me.activeChurchId]);
  const appts = useAsync((signal) => (me.activeChurchId ? api.get<{ items: Appointment[] }>(`/multichurch/churches/${me.activeChurchId}/appointments`, signal).then((r) => r.items) : Promise.resolve([])), [me.activeChurchId]);
  const titlesOf = (personId: string) => (appts.status === 'ready' ? appts.data.filter((a) => a.personId === personId).map(titleLabel) : []);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  return (
    <div className="page">
      <p className="small"><Link to="/app/ministerios">← Ministerios</Link></p>
      <header className="page__header">
        <h1>Organigrama</h1>
        <div className="actions"><Button variant="secondary" onClick={() => window.print()}>Imprimir</Button></div>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        <div className="orgchart">
          <div className="orgchart__node orgchart__node--root">{churchName}</div>
          <div className="orgchart__node orgchart__node--pastoral">
            <strong>Pastoral</strong>
            <span className="small">{data.data.pastoral.map((p) => (titlesOf(p.personId).length ? `${titlesOf(p.personId).join(', ')} ${p.name}` : p.name)).join(' · ') || '—'}</span>
          </div>
          {appts.status === 'ready' && appts.data.some((a) => !data.data!.pastoral.some((p) => p.personId === a.personId)) ? (
            <section className="orgchart__group">
              <h2 className="small muted">Nombramientos</h2>
              <ul className="orgchart__list">
                {appts.data.filter((a) => !data.data!.pastoral.some((p) => p.personId === a.personId)).map((a) => (
                  <li key={a.id} className="orgchart__node"><strong>{titleLabel(a)}</strong><span className="small">{a.name}</span></li>
                ))}
              </ul>
            </section>
          ) : null}
          {KIND.map(([k, label]) => {
            const list = data.data!.ministries.filter((m) => m.kind === k);
            if (list.length === 0) return null;
            return (
              <section key={k} className="orgchart__group">
                <h2 className="small muted">{label}</h2>
                <ul className="orgchart__list">
                  {list.map((m) => (
                    <li key={m.id} className="orgchart__node">
                      <Link to={`/app/ministerios/${m.id}`}><strong>{m.name}</strong></Link>
                      <span className="small">{m.leader ? `Líder: ${m.leader.name}` : 'Sin líder'}</span>
                      {m.coLeaders.length ? <span className="small muted">Co-líder{m.coLeaders.length > 1 ? 'es' : ''}: {m.coLeaders.map((c) => c.name).join(', ')}</span> : null}
                      <span className="small muted">{m.members} {m.members === 1 ? 'integrante' : 'integrantes'}</span>
                      {m.cells.length ? (
                        <ul className="orgchart__cells">
                          {m.cells.map((c) => (
                            <li key={c.id}>
                              Célula {c.name} · {c.members}{(c.representatives ?? (c.representative ? [c.representative] : [])).length ? ` · ${(c.representatives ?? [c.representative!]).map((r) => r.name).join(', ')}` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
